import { useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { Button } from "@cieda/djayma-ui";
import { LaunchCountdown } from "@/components/admin/launch/LaunchCountdown";
import { LaunchPhaseTab } from "@/components/admin/launch/LaunchPhaseTab";
import { useLaunchTasks, useUpdateLaunchTask, useCreateLaunchTask } from "@/hooks/admin/useLaunch";
import { useAdminUsers } from "@/hooks/admin/useOperations";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import type { LaunchTask, LaunchPhase, LaunchStatus, LaunchPriority, CreateLaunchTaskInput } from "@/lib/admin-api/launch";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const J_DAY = new Date("2026-06-01T00:00:00Z");

const PHASES: { key: LaunchPhase; label: string; description: string }[] = [
  { key: "technique",  label: "Technique",  description: "Infrastructure, paiements et déploiement · Sans ça, rien ne fonctionne" },
  { key: "catalogue",  label: "Catalogue",  description: "Produits, photos, prix et descriptions" },
  { key: "agents",     label: "Agents",     description: "Formation et outils des 13 agents terrain" },
  { key: "marketing",  label: "Marketing",  description: "Acquisition, WhatsApp et réseaux sociaux" },
  { key: "produits",   label: "Produits",   description: "Bundles, packs et offres de lancement" },
];

// ---------------------------------------------------------------------------
// Edit task modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  readonly task: LaunchTask | null;
  readonly onClose: () => void;
  readonly onSave: (id: string, title: string, description: string) => Promise<void>;
}

function EditTaskModal({ task, onClose, onSave }: EditModalProps) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [saving, setSaving] = useState(false);

  // The modal stays mounted across open/close cycles (AdminModal drives
  // visibility via `open`), so the useState initializers only run once.
  // Without this effect the fields kept the previous task's values — or
  // empty strings from the initial `task=null` mount — and saving would
  // silently overwrite the real task with blanks. Bug #8 in the Phase 2
  // QA report.
  useEffect(() => {
    if (task) {
      setTitle(task.title ?? "");
      setDescription(task.description ?? "");
    }
  }, [task]);

  const handleSave = async () => {
    if (!task || !title.trim()) return;
    setSaving(true);
    try {
      await onSave(task.id, title.trim(), description.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      open={!!task}
      onClose={onClose}
      title="Modifier la tâche"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="edit-task-title" className="mb-1 block text-sm font-medium text-stone-700">Titre</label>
          <input
            id="edit-task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-terracotta-400 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="edit-task-desc" className="mb-1 block text-sm font-medium text-stone-700">Description</label>
          <textarea
            id="edit-task-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-terracotta-400 focus:outline-none"
          />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Create task modal
// ---------------------------------------------------------------------------

interface CreateModalProps {
  readonly open: boolean;
  readonly defaultPhase: LaunchPhase;
  readonly onClose: () => void;
  readonly onSave: (input: CreateLaunchTaskInput) => Promise<void>;
}

function CreateTaskModal({ open, defaultPhase, onClose, onSave }: CreateModalProps) {
  const [phase, setPhase] = useState<LaunchPhase>(defaultPhase);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<LaunchPriority>("critique");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({ phase, title: title.trim(), description: description.trim() || undefined, priority });
      setTitle(""); setDescription(""); setPriority("critique");
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Ajouter une tâche"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? "Création…" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="create-task-phase" className="mb-1 block text-sm font-medium text-stone-700">Phase</label>
          <select
            id="create-task-phase"
            value={phase}
            onChange={(e) => setPhase(e.target.value as LaunchPhase)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm"
          >
            {PHASES.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="create-task-title" className="mb-1 block text-sm font-medium text-stone-700">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            id="create-task-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-terracotta-400 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="create-task-desc" className="mb-1 block text-sm font-medium text-stone-700">Description</label>
          <textarea
            id="create-task-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="create-task-priority" className="mb-1 block text-sm font-medium text-stone-700">Priorité</label>
          <select
            id="create-task-priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as LaunchPriority)}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm"
          >
            <option value="critique">Critique</option>
            <option value="best_practice">Best practice</option>
          </select>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function LaunchDashboard() {
  const { t } = useTranslation("admin");
  useAdminPageTitle(t("nav.lancement", "Lancement J-Day"));

  const [activePhase, setActivePhase] = useState<LaunchPhase>("technique");
  const [editingTask, setEditingTask] = useState<LaunchTask | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [currentAdminId, setCurrentAdminId] = useState<string>("");

  // Get current admin id once
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) setCurrentAdminId(session.user.id);
    });
  }, []);

  const { data, isLoading, isError } = useLaunchTasks({});
  const updateTask = useUpdateLaunchTask();
  const createTask = useCreateLaunchTask();
  const { data: usersData } = useAdminUsers({ limit: 100 });

  const allTasks = data?.tasks ?? [];
  const adminUsers = (usersData?.users ?? []).map((u) => ({
    id: u.id,
    full_name: u.full_name,
    email: u.email,
  }));

  // Global stats
  const totalDone = allTasks.filter((t) => t.status === "done").length;
  const criticalLeft = allTasks.filter((t) => t.priority === "critique" && t.status !== "done").length;
  const pctGlobal = allTasks.length > 0 ? Math.round((totalDone / allTasks.length) * 100) : 0;

  // Tasks per active phase
  const phaseTasks = useMemo(
    () => allTasks.filter((t) => t.phase === activePhase),
    [allTasks, activePhase],
  );

  // Phase tab counters
  const phaseCounts = useMemo(
    () =>
      Object.fromEntries(
        PHASES.map((p) => {
          const pts = allTasks.filter((t) => t.phase === p.key);
          const done = pts.filter((t) => t.status === "done").length;
          return [p.key, { total: pts.length, done }];
        }),
      ) as Record<LaunchPhase, { total: number; done: number }>,
    [allTasks],
  );

  const handleToggle = useCallback(
    async (id: string, newStatus: LaunchStatus) => {
      try {
        await updateTask.mutateAsync({ id, input: { status: newStatus } });
      } catch {
        toast.error("Impossible de mettre à jour la tâche.");
      }
    },
    [updateTask],
  );

  const handleAssign = useCallback(
    async (id: string, userId: string | null) => {
      try {
        await updateTask.mutateAsync({ id, input: { assigned_to: userId } });
      } catch {
        toast.error("Impossible d'assigner la tâche.");
      }
    },
    [updateTask],
  );

  const handleEditSave = useCallback(
    async (id: string, title: string, description: string) => {
      try {
        await updateTask.mutateAsync({ id, input: { title, description } });
      } catch {
        toast.error("Impossible de modifier la tâche.");
      }
    },
    [updateTask],
  );

  const handleCreate = useCallback(
    async (input: CreateLaunchTaskInput) => {
      try {
        await createTask.mutateAsync(input);
      } catch {
        toast.error("Impossible de créer la tâche.");
      }
    },
    [createTask],
  );

  const activePhaseInfo = PHASES.find((p) => p.key === activePhase) ?? PHASES[0];

  return (
    <AdminLayout>
      {/* Page header */}
      <div className="border-b border-stone-200 bg-white">
        <div className="px-7 pb-0 pt-6">
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h1 className="text-xl font-black tracking-tight text-stone-900">
                Lancement Djayma
                <span className="ml-2 text-sm font-normal text-stone-400">— J-Day</span>
              </h1>
              <p className="mt-1 text-xs text-stone-400">
                Suivi des {allTasks.length} tâches avant l&apos;ouverture · Assignation par membre de l&apos;équipe
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setCreateOpen(true)}>+ Ajouter une tâche</Button>
            </div>
          </div>

          {/* Countdown + global progress */}
          <div className="mb-5 grid grid-cols-[auto_1fr_auto_auto] items-center gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Ouverture dans</p>
              <LaunchCountdown targetDate={J_DAY} />
              <p className="text-[10px] text-stone-400">1 juin 2026</p>
            </div>

            <div>
              <div className="mb-1.5 flex justify-between text-xs text-stone-400">
                <span>Progression globale</span>
                <strong className="text-stone-600">{totalDone} / {allTasks.length} tâches complétées</strong>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-terracotta-500 to-amber-400 transition-all duration-500"
                  style={{ width: `${pctGlobal}%` }}
                />
              </div>
              <p className="mt-1 text-[10px] text-stone-400">
                {pctGlobal}% · {allTasks.length - totalDone} tâches restantes{criticalLeft > 0 ? ` dont ${criticalLeft} critiques` : ""}
              </p>
            </div>

            <div className="rounded-lg bg-emerald-50 px-4 py-2 text-center">
              <p className="text-xl font-black text-emerald-600">{totalDone}</p>
              <p className="text-[10px] text-stone-400">Complétées</p>
            </div>

            <div className="rounded-lg bg-amber-50 px-4 py-2 text-center">
              <p className="text-xl font-black text-terracotta-600">{criticalLeft}</p>
              <p className="text-[10px] text-stone-400">Critiques restantes</p>
            </div>
          </div>

          {/* Phase tabs */}
          <div role="tablist" className="-mb-px flex gap-0">
            {PHASES.map((phase) => {
              const counts = phaseCounts[phase.key];
              const isActive = activePhase === phase.key;
              return (
                <button
                  key={phase.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActivePhase(phase.key)}
                  className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-xs font-semibold transition-colors ${
                    isActive
                      ? "border-terracotta-500 text-terracotta-600"
                      : "border-transparent text-stone-500 hover:text-stone-700"
                  }`}
                >
                  <span>{phase.label}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                    isActive ? "bg-amber-100 text-terracotta-700" : "bg-stone-100 text-stone-500"
                  }`}>
                    {counts.done}/{counts.total}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-stone-400">Chargement…</p>
        </div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-red-500">Erreur lors du chargement des tâches.</p>
        </div>
      ) : (
        <LaunchPhaseTab
          phaseLabel={`Phase — ${activePhaseInfo.label}`}
          phaseDescription={activePhaseInfo.description}
          tasks={phaseTasks}
          adminUsers={adminUsers}
          currentAdminId={currentAdminId}
          onToggle={handleToggle}
          onAssign={handleAssign}
          onEdit={setEditingTask}
        />
      )}

      {/* Modals */}
      <EditTaskModal
        task={editingTask}
        onClose={() => setEditingTask(null)}
        onSave={handleEditSave}
      />
      <CreateTaskModal
        open={createOpen}
        defaultPhase={activePhase}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />
    </AdminLayout>
  );
}

export default LaunchDashboard;
